
import { useEffect } from "react";
import { Link } from "react-router-dom";

const sections = [
  {
    title: "1. Information We Collect",
    intro: "The NPO collects the following information through appropriate means:",
    items: [
      "Name, address, phone number, and email address.",
      "Identity verification documents such as passports (for foreign nationals).",
      "Payment information (Credit card details are processed securely via Stripe; the NPO does not store complete card information).",
      "Service usage history and inquiry details.",
    ],
  },
  {
    title: "2. Purpose of Use",
    intro: "We use the collected personal information for the following purposes:",
    items: [
      "Management of reservations, contract execution (via DocuSign, etc.), and identity verification.",
      "Processing payments for stay fees and other charges (via Stripe).",
      "Communication, notifications, and responding to inquiries regarding the Service.",
      "Creation and maintenance of guest registers as required by law.",
    ],
  },
  {
    title: "3. Sharing Information with Third Parties",
    intro:
      "The NPO will not provide personal information to third parties except in the following cases:",
    items: [
      "When the user has given prior consent.",
      "When disclosing to service providers (e.g., Stripe, DocuSign) within the scope necessary to fulfill the purpose of use.",
      "When required by law.",
    ],
  },
  {
    title: "4. Data Security",
    intro:
      "The NPO implements appropriate security measures and manages personal information strictly to prevent leakage, loss, or damage.",
    items: [],
  },
];

export default function PrivacyPolicy() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div
      className="min-h-screen bg-white"
      style={{ fontFamily: "'Inter', sans-serif" }}
    >
      {/* Header */}
      <header className="w-full bg-stone-900 text-white">
        <div className="max-w-5xl mx-auto px-6 py-5 flex items-center justify-between">
          <Link
            to="/"
            className="flex items-center gap-3 cursor-pointer hover:opacity-80 transition-opacity"
          >
            <img
              src="https://static.readdy.ai/image/44752aaa2b8c60b6ac39ccbed2793a85/f0377fb5ec1076e4cb9e676fd4f0df74.jpeg"
              alt="Coliving HIDA TAKAYAMA"
              className="w-9 h-9 rounded-full object-cover"
            />
            <span
              className="text-base tracking-widest font-light"
              style={{ fontFamily: "'Noto Serif JP', serif" }}
            >
              COLIVING HIDA TAKAYAMA
            </span>
          </Link>
          <Link
            to="/"
            className="text-sm text-gray-300 hover:text-orange-400 transition-colors cursor-pointer flex items-center gap-1"
          >
            <i className="ri-arrow-left-line w-4 h-4 flex items-center justify-center"></i>
            Back to Home
          </Link>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-5xl mx-auto px-6 py-16">
        <h1
          className="text-2xl font-semibold text-stone-800 mb-2"
          style={{ fontFamily: "'Noto Serif JP', serif" }}
        >
          Privacy Policy
        </h1>
        <p className="text-sm text-gray-600 mb-10 leading-relaxed max-w-3xl">
          NPO MOSHIMOSHI (hereinafter referred to as &quot;the NPO&quot;) establishes
          this Privacy Policy regarding the handling of users&apos; personal
          information for the &quot;Coliving HIDA TAKAYAMA&quot; (hereinafter referred to
          as &quot;the Service&quot;).
        </p>

        <div className="space-y-10">
          {sections.map((section) => (
            <div key={section.title}>
              <h2 className="text-lg font-semibold text-stone-800 mb-3">
                {section.title}
              </h2>
              <p className="text-sm text-stone-600 leading-relaxed mb-3">
                {section.intro}
              </p>
              {section.items.length > 0 && (
                <ul className="space-y-2">
                  {section.items.map((item) => (
                    <li
                      key={item}
                      className="flex items-start gap-3 text-sm text-stone-600 leading-relaxed"
                    >
                      <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-orange-400 shrink-0"></span>
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          ))}

          {/* Contact Section */}
          <div>
            <h2 className="text-lg font-semibold text-stone-800 mb-3">
              5. Contact Us
            </h2>
            <p className="text-sm text-stone-600 leading-relaxed mb-4">
              For inquiries regarding the disclosure, correction, or deletion of
              personal information, please contact:
            </p>
            <div className="bg-stone-50 border border-gray-200 rounded-lg p-6 space-y-2 text-sm text-stone-600">
              <p className="font-semibold text-stone-800">NPO MOSHIMOSHI</p>
              <p>
                Paulownia Building 3F, 3-58-11 Honmachi, Takayama City, Gifu
                506-0011, Japan
              </p>
              <p>
                Email:{" "}
                <a
                  href="mailto:info@moshimoshi.info"
                  className="text-orange-600 hover:text-orange-700 underline underline-offset-2 cursor-pointer"
                >
                  info@moshimoshi.info
                </a>
              </p>
            </div>
          </div>
        </div>

        <div className="mt-12 text-center">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-sm text-stone-500 hover:text-orange-500 transition-colors cursor-pointer"
          >
            <i className="ri-arrow-left-line w-4 h-4 flex items-center justify-center"></i>
            Back to Home
          </Link>
        </div>
      </main>

      {/* Footer */}
      <footer className="bg-stone-100 border-t border-gray-200">
        <div className="max-w-5xl mx-auto px-6 py-8 text-center text-xs text-gray-400">
          &copy; {new Date().getFullYear()} NPO MOSHIMOSHI. All rights
          reserved.
          <span className="mx-2">&bull;</span>
          <a
            href="https://readdy.ai/?ref=logo"
            rel="nofollow"
            className="hover:text-orange-500 transition-colors cursor-pointer"
          >
            Powered by Readdy
          </a>
        </div>
      </footer>
    </div>
  );
}
